/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { OvertimeReviewRow, ReceiptReviewRow } from './TripClaimRows';
import { isPendingReceipt, tripClaimsMessage } from '../../Utils/tripClaims';
import { isPendingOvertime, overtimeEntryId } from '../../Utils/driverOvertime';

/**
 * The top of Order Details while trip claims wait for review: what is
 * waiting, each with approve / reject right here (the Trip claims page's own
 * rows), and the way to the booking's Driver Events tab — the one place per
 * booking where claims are reviewed, with their history.
 *
 * `claims` is the booking's useBookingClaims state, shared with that tab.
 */
function TripClaimsBanner({ claims, onOpenDriverEvents, onCreateLineItem }) {
  const { counts, busyId } = claims;
  // Claims decided from this banner stay in it, so "Create line item" is
  // right there after an approval.
  const [decidedHere, setDecidedHere] = useState(() => new Set());
  if (!counts?.total && decidedHere.size === 0) return null;

  const decide = async (review) => {
    const id = await review();
    if (id) setDecidedHere((prev) => new Set(prev).add(id));
  };
  const shown = (id, pending) => pending || decidedHere.has(id);
  const receipts = claims.expenses.filter((e) => shown(e._id, isPendingReceipt(e)));
  const overtime = claims.overtime.filter((e) => shown(overtimeEntryId(e), isPendingOvertime(e)));

  return (
    <Alert variant={counts.total ? 'warning' : 'success'} className="mb-3" data-testid="trip-claims-banner">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="fw-semibold">
          <i className={`fa ${counts.total ? 'fa-exclamation-triangle' : 'fa-check'} me-2`} />
          {counts.total ? tripClaimsMessage(counts) : 'Every trip claim on this booking has been reviewed'}
        </div>
        <Button size="sm" variant="outline-dark" onClick={onOpenDriverEvents}>
          <i className="fa fa-list me-1" />
          Driver events
        </Button>
      </div>
      {counts.total > 0 && (
        <div className="small mt-1">This booking can&apos;t be completed until each one is approved or rejected.</div>
      )}
      {claims.error && <div className="small text-danger mt-1" role="alert">{claims.error}</div>}
      {(receipts.length > 0 || overtime.length > 0) && (
        <div className="bg-white rounded border px-2 mt-2">
          {overtime.map((entry, index) => {
            const id = overtimeEntryId(entry);
            return (
              <OvertimeReviewRow
                // eslint-disable-next-line react/no-array-index-key
                key={id || index}
                entry={entry}
                busy={busyId === id}
                onReview={(decision) => decide(() => claims.reviewOvertime(entry, decision))}
                onCreateLineItem={() => onCreateLineItem({ kind: 'overtime', entry })}
              />
            );
          })}
          {receipts.map((expense) => (
            <ReceiptReviewRow
              key={expense._id}
              expense={expense}
              busy={busyId === expense._id}
              onReview={(decision) => decide(() => claims.reviewReceipt(expense, decision))}
              onCreateLineItem={() => onCreateLineItem({ kind: 'receipt', expense })}
              onOpenPhoto={claims.openPhoto}
            />
          ))}
        </div>
      )}
    </Alert>
  );
}

export default TripClaimsBanner;
